import { useEffect, useMemo, useRef, useState } from "react";
import type { UseProgressResult } from "@/core/hooks/useProgress";
import type { CourseModule, Lesson } from "@/core/types";
import { Icon } from "./Icon";
import { StepTrack } from "./Progress";
import { StepCard } from "./StepCard";

interface LessonViewProps {
  module: CourseModule;
  lesson: Lesson;
  progress: UseProgressResult;
  onBack(): void;
  onNextLesson?: () => void;
  nextLessonTitle?: string;
}

/**
 * Eine Lektion, Schritt für Schritt. Oben stehen Titel und Schrittband, darunter
 * genau ein Schritt; die Aktionen des Schritts sitzen fest am unteren Rand.
 * Sind alle Schritte erledigt, folgt als letzte Seite der Abschluss.
 */
export function LessonView({
  module,
  lesson,
  progress,
  onBack,
  onNextLesson,
  nextLessonTitle,
}: LessonViewProps) {
  const stepIds = useMemo(() => lesson.steps.map((step) => step.id), [lesson]);
  const doneCount = progress.completedInLesson(module.id, lesson.id, stepIds);
  const allDone = doneCount === lesson.steps.length;
  const completionIndex = lesson.steps.length;

  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState<"forward" | "back">("forward");
  const topRef = useRef<HTMLDivElement>(null);

  // Beim Öffnen einer Lektion direkt beim ersten offenen Schritt starten –
  // oder beim Abschluss, wenn alles erledigt ist.
  useEffect(() => {
    const firstOpen = lesson.steps.findIndex(
      (step) => !progress.isStepDone(module.id, lesson.id, step.id),
    );
    setDirection("forward");
    setIndex(firstOpen === -1 ? completionIndex : firstOpen);
    progress.rememberLesson(module.id, lesson.id);
    // Nur beim Lektionswechsel neu bestimmen, nicht bei jedem Fortschritts-Update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module.id, lesson.id]);

  const goTo = (target: number) => {
    if (target === index) return;
    setDirection(target > index ? "forward" : "back");
    setIndex(target);
    // Der neue Schritt beginnt oben, das Schrittband bleibt dabei im Blick.
    const top = topRef.current;
    if (top && top.getBoundingClientRect().top < 0) {
      top.scrollIntoView({ block: "start" });
    }
  };

  /** Nach dem letzten Schritt: zum Abschluss, oder zurück zu einem übersprungenen Schritt. */
  const goNext = (fromStepId: string) => {
    if (index < lesson.steps.length - 1) {
      goTo(index + 1);
      return;
    }
    const firstOpen = lesson.steps.findIndex(
      (step) => step.id !== fromStepId && !progress.isStepDone(module.id, lesson.id, step.id),
    );
    goTo(firstOpen === -1 ? completionIndex : firstOpen);
  };

  const showCompletion = index >= completionIndex && allDone;
  // Steht die Anzeige auf dem Abschluss, obwohl wieder etwas offen ist, zeigt
  // sie den letzten Schritt statt einer leeren Seite.
  const current = lesson.steps[Math.min(index, lesson.steps.length - 1)];

  return (
    <div className="lesson">
      <header className="lesson-head" ref={topRef}>
        <h1>{lesson.title}</h1>
        <p className="lesson-head__sub">
          {module.title} · {lesson.durationMinutes} Min.
        </p>

        <div className="lesson-head__track">
          <StepTrack
            steps={lesson.steps.map((step) => ({
              id: step.id,
              title: step.title,
              done: progress.isStepDone(module.id, lesson.id, step.id),
            }))}
            current={showCompletion ? -1 : index}
            onSelect={goTo}
          />
          <p className="lesson-head__count num" aria-live="polite">
            {showCompletion
              ? `${doneCount} von ${lesson.steps.length} erledigt`
              : `Schritt ${index + 1} von ${lesson.steps.length}`}
          </p>
        </div>

        {lesson.learningGoals.length > 0 ? (
          <details className="disclosure goals">
            <summary>
              <Icon name="target" />
              <span>Lernziele dieser Lektion</span>
              <Icon name="chevronDown" className="disclosure__chevron" />
            </summary>
            <ul className="goals__list">
              {lesson.learningGoals.map((goal) => (
                <li key={goal}>{goal}</li>
              ))}
            </ul>
          </details>
        ) : null}
      </header>

      {showCompletion ? (
        <div className={`stage stage--${direction}`} key="abschluss">
          <section className="complete" aria-labelledby="abschluss-titel">
            <svg className="complete__mark" width="56" height="56" viewBox="0 0 56 56" aria-hidden="true">
              <circle cx="28" cy="28" r="26" pathLength={1} />
              <path d="M17 29.5 24.5 37 39.5 20" pathLength={1} />
            </svg>
            <h2 id="abschluss-titel">Lektion abgeschlossen</h2>
            <p className="complete__sub">
              Alle {lesson.steps.length} Schritte von „{lesson.title}“ sind erledigt.
            </p>
            {lesson.learningGoals.length > 0 ? (
              <>
                <h3 className="complete__label">Das kannst du jetzt</h3>
                <ul className="checklist">
                  {lesson.learningGoals.map((goal, goalIndex) => (
                    <li key={goal} style={{ ["--i" as string]: goalIndex }}>
                      <Icon name="check" />
                      <span>{goal}</span>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </section>

          <div className="actionbar">
            <button type="button" className="btn btn--secondary" onClick={onBack}>
              Übersicht
            </button>
            {onNextLesson ? (
              <button
                type="button"
                className="btn btn--primary btn--grow"
                onClick={onNextLesson}
                title={nextLessonTitle ? `Weiter mit „${nextLessonTitle}“` : undefined}
              >
                <span className="btn__label">Nächste Lektion</span>
                <Icon name="arrowRight" />
              </button>
            ) : (
              <button type="button" className="btn btn--primary btn--grow" onClick={onBack}>
                Zur Kursübersicht
              </button>
            )}
          </div>
        </div>
      ) : current ? (
        <div className={`stage stage--${direction}`} key={current.id}>
          <StepCard
            step={current}
            isLast={index === lesson.steps.length - 1}
            isDone={progress.isStepDone(module.id, lesson.id, current.id)}
            onPrev={index > 0 ? () => goTo(index - 1) : undefined}
            onCompleted={() => progress.markStepDone(module.id, lesson.id, current.id)}
            onNext={() => goNext(current.id)}
          />
        </div>
      ) : null}
    </div>
  );
}
